/* Conservative progressive enhancement: all source facts remain available without JS. */
(() => {
  'use strict';
  const text = node => (node?.textContent || '').replace(/\s+/g, ' ').trim();
  const key = value => value.replace(/[:：]\s*$/, '').replace(/\s+/g, ' ').trim();
  const identityKey = value => /^(tuotenumero|artikelnummer|sku|gtin\s*\/\s*ean|ean|gtin|väri\s*\/\s*viimeistely|färg\s*\/\s*ytfinish)$/i.test(value);
  const activeRoots = new WeakMap();
  function rowsFrom(list) {
    const items = Array.from(list.children);
    if (!items.length || items.some(item => item.tagName !== 'LI')) return null;
    const rows = items.map(item => {
      const label = item.firstElementChild;
      if (!label || label.tagName !== 'STRONG' || !/:\s*$/.test(text(label))) return null;
      const clone = item.cloneNode(true);
      clone.firstElementChild.remove();
      if (!text(clone)) return null;
      return { label: key(text(label)), html: clone.innerHTML.trim(), value: text(clone) };
    });
    return rows.every(Boolean) ? rows : null;
  }
  function specList(rows) {
    const dl = document.createElement('dl');
    dl.className = 'nlc-specs';
    rows.forEach(row => {
      const div = document.createElement('div');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = row.label;
      dd.innerHTML = row.html;
      div.append(dt, dd); dl.append(div);
    });
    return dl;
  }
  function disclosure(label, className = '') {
    const details = document.createElement('details');
    details.className = `nlc-details ${className}`.trim();
    const summary = document.createElement('summary');
    summary.textContent = label;
    const body = document.createElement('div'); body.className = 'nlc-details__body';
    details.append(summary, body);
    return { details, body };
  }
  function canonicalLink(value) {
    try {
      const url = new URL(value, location.origin);
      if (url.pathname.includes('/products/') && url.searchParams.has('variant')) return 'variant:' + url.searchParams.get('variant');
      return decodeURI(url.pathname).replace(/\/$/, '');
    } catch (_) { return value; }
  }
  function compactLabels(root, description) {
    const items = Array.from(root.querySelectorAll('.nlc-picker__item'));
    if (items.length < 2) return;
    const labels = new Map();
    description?.querySelectorAll('.nl-variant-overview [role="listitem"]').forEach(card => {
      const strong = card.querySelector('strong');
      const rest = text(card).replace(text(strong), '').trim();
      const sku = rest.match(/(?:Tuotenumero|Artikelnummer)\s*:?\s*([\w-]+)/i)?.[1];
      if (sku && strong) labels.set(sku, text(strong));
    });
    description?.querySelectorAll('h3').forEach(heading => {
      const match = text(heading).match(/^([A-Za-z]{0,6}\d[\w-]*)\s*[–—]\s*(.+)$/);
      if (match) labels.set(match[1], match[2]);
    });
    const originals = items.map(item => text(item.querySelector('[data-nlc-option-label]')));
    const tokens = originals.map(value => value.split(' '));
    let prefix = 0, suffix = 0;
    while (tokens.every(parts => parts.length > prefix + 1 && parts[prefix] === tokens[0][prefix])) prefix++;
    while (tokens.every(parts => parts.length > prefix + suffix + 1 && parts[parts.length - 1 - suffix] === tokens[0][tokens[0].length - 1 - suffix])) suffix++;
    const shortened = tokens.map(parts => parts.slice(prefix, suffix ? -suffix : undefined).join(' '));
    const canShorten = new Set(shortened).size === items.length && shortened.every(Boolean);
    items.forEach((item, index) => {
      const label = item.querySelector('[data-nlc-option-label]');
      const replacement = labels.get(item.dataset.nlcSku) || (canShorten ? shortened[index] : originals[index]);
      if (label && replacement) label.textContent = replacement;
    });
  }
  function preservePurinaRecommendations(root, description) {
    const nativeLinks = new Set(Array.from(root.querySelectorAll('nl4-bulb-choice a[href]')).map(a => canonicalLink(a.getAttribute('href'))));
    if (!nativeLinks.size) return;
    const headings = /^(valmistajan suosittelemat lamput|tillverkarens rekommenderade ljuskällor|ljuskällor som tillverkaren rekommenderar)$/i;
    description.querySelectorAll('h3').forEach(heading => {
      if (!headings.test(text(heading))) return;
      const list = heading.nextElementSibling;
      if (list?.tagName !== 'UL') return;
      const links = Array.from(list.querySelectorAll('a[href]'));
      if (!links.length || !links.every(a => nativeLinks.has(canonicalLink(a.getAttribute('href'))))) return;
      /* Retain exact recommendation wording inside a closed disclosure. */
      links.forEach(link => {
        const canonical = canonicalLink(link.getAttribute('href'));
        const local = Array.from(root.querySelectorAll('nl4-bulb-choice a[href]')).find(a => canonicalLink(a.getAttribute('href')) === canonical);
        if (local) link.setAttribute('href', local.getAttribute('href'));
      });
      const note = disclosure(text(heading), 'nlc-recommendation');
      heading.replaceWith(note.details); note.body.append(list);
    });
  }
  function removeDuplicateBulbs(root, description) {
    const variants = new Set();
    const paths = new Set();
    const record = value => {
      try {
        const url = new URL(value, location.origin);
        if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
        paths.add(decodeURI(url.pathname).replace(/\/$/, ''));
        if (url.searchParams.has('variant')) variants.add(url.searchParams.get('variant'));
      } catch (_) {}
    };
    root.querySelectorAll('nl4-bulb-choice a[href], nl4-track-choice a[href]').forEach(a => record(a.getAttribute('href')));
    root.querySelectorAll('nl4-track-choice option[data-url]').forEach(option => record(option.dataset.url));
    const repeated = link => {
      try {
        const url = new URL(link.getAttribute('href'), location.origin);
        if (url.origin !== location.origin) return false;
        return url.searchParams.has('variant') ? variants.has(url.searchParams.get('variant')) : paths.has(decodeURI(url.pathname).replace(/\/$/, ''));
      } catch (_) { return false; }
    };
    const headings = /^(valmistajan suosittelemat lamput|tillverkarens rekommenderade ljuskällor|ljuskällor som tillverkaren rekommenderar)$/i;
    description.querySelectorAll('h3').forEach(heading => {
      if (!headings.test(text(heading))) return;
      const list = heading.nextElementSibling;
      if (list?.tagName !== 'UL') return;
      const links = Array.from(list.querySelectorAll('a[href]'));
      if (links.length && links.every(repeated)) { heading.remove(); list.remove(); }
    });
    description.querySelectorAll('p').forEach(paragraph => {
      const links = Array.from(paragraph.querySelectorAll('a[href]'));
      if (!links.length || !links.every(repeated)) return;
      const rest = paragraph.cloneNode(true); rest.querySelectorAll('a').forEach(a => a.remove());
      if (/^(sopiva lamppu|passande ljuskälla):\s*\.?$/i.test(text(rest))) paragraph.remove();
    });
  }
  function enhance(root) {
    const description = root.querySelector('[data-nlc-description]');
    compactLabels(root, description);
    if (!description || description.querySelector('.nlbs')) return;
    const previous = activeRoots.get(root);
    if (previous?.description === description) return;
    previous?.controller.abort();
    const controller = new AbortController();
    activeRoots.set(root, { description, controller });
    const variantLabels = new Map(Array.from(root.querySelectorAll('.nlc-picker__item')).map(item => [item.dataset.nlcSku, text(item.querySelector('[data-nlc-option-label]'))]));
    description.querySelectorAll('h4').forEach(heading => {
      const list = heading.nextElementSibling;
      if (list?.tagName !== 'UL') return;
      const rows = rowsFrom(list);
      const sku = rows?.find(row => /^(tuotenumero|artikelnummer|sku)$/i.test(row.label))?.value;
      const label = variantLabels.get(sku);
      if (label) { heading.title = text(heading); heading.textContent = label; }
    });
    const groups = [];
    description.querySelectorAll('ul:not(.nl4-product-copy__highlights)').forEach(list => {
      if (list.closest('.nl-product-documents, .nl-variant-overview')) return;
      const rows = rowsFrom(list);
      if (!rows || !rows.length) return;
      const heading = list.previousElementSibling;
      const match = heading?.tagName === 'H3' && text(heading).match(/^([A-Za-z]{0,6}\d[\w-]*)\s*[–—]\s*(.+)$/);
      const hasSku = rows.some(row => /^(tuotenumero|artikelnummer|sku)$/i.test(row.label));
      if ((match && rows.some(row => /^(tuotenumero|artikelnummer|sku)$/i.test(row.label) && row.value === match[1])) || (Number(description.dataset.variantCount) === 1 && hasSku && /^H[234]$/.test(heading?.tagName || ''))) {
        groups.push({ list, heading, rows });
      } else if (!list.classList.contains('nl-product-spec-grid')) {
        list.replaceWith(specList(rows));
      }
    });
    const sameRow = (a, b) => a.label === b.label && a.html === b.html;
    if (groups.length >= 1 && groups.length === Number(description.dataset.variantCount) && groups.every(group => group.list.parentElement === groups[0].list.parentElement)) {
      const shared = groups[0].rows.filter(row => !identityKey(row.label) && groups.every(group => group.rows.some(other => sameRow(row, other))));
      if (shared.length) {
        const heading = document.createElement('h3'); heading.textContent = groups.length > 1 ? description.dataset.sharedSpecs : (description.dataset.specsLabel || description.dataset.sharedSpecs);
        groups[0].heading.before(heading, specList(shared));
      }
      const identity = disclosure(description.dataset.variantsLabel, 'nlc-variant-details');
      const selectedFacts = document.createElement('div'); selectedFacts.className = 'nlc-selected-specs';
      groups[0].heading.before(selectedFacts, identity.details);
      const entries = [];
      groups.forEach(group => {
        const sku = group.rows.find(row => /^(tuotenumero|artikelnummer|sku)$/i.test(row.label))?.value;
        const label = variantLabels.get(sku) || text(group.heading).replace(/^[A-Za-z]{0,6}\d[\w-]*\s*[–—]\s*/, '');
        const identifiers = group.rows.filter(row => identityKey(row.label));
        const facts = group.rows.filter(row => !identityKey(row.label) && !shared.some(other => sameRow(row, other)));
        if (identifiers.length) {
          const heading = document.createElement('h3'); heading.textContent = label;
          identity.body.append(heading, specList(identifiers));
        }
        if (facts.length) {
          const section = document.createElement('section'); section.dataset.specSku = sku;
          const heading = document.createElement('h3'); heading.textContent = label;
          section.append(heading, specList(facts)); selectedFacts.append(section); entries.push(section);
        }
        group.heading.remove(); group.list.remove();
      });
      const updateSpecs = () => {
        let sku = root.querySelector('.nlc-picker__input:checked')?.closest('[data-nlc-sku]')?.dataset.nlcSku;
        if (!sku) {
          try { sku = JSON.parse(root.querySelector('[data-js-variant-data]')?.textContent || '{}').sku; } catch (_) {}
        }
        const known = sku && groups.some(group => group.rows.some(row => /^(tuotenumero|artikelnummer|sku)$/i.test(row.label) && row.value === sku));
        entries.forEach(entry => { entry.hidden = Boolean(known && entry.dataset.specSku !== sku); });
      };
      root.addEventListener('change', updateSpecs, { signal: controller.signal });
      root.addEventListener('VARIANT_CHANGE', updateSpecs, { capture: true, signal: controller.signal });
      root.addEventListener('reload', updateSpecs, { signal: controller.signal });
      updateSpecs();
      if (!identity.body.children.length) identity.details.remove();
      if (!entries.length) selectedFacts.remove();
    } else {
      groups.forEach(group => group.list.replaceWith(specList(group.rows)));
    }
    description.querySelectorAll('.nl-product-documents, .nl-variant-overview').forEach(section => {
      if (section.closest('.nlc-details')) return;
      const heading = section.querySelector('h2,h3');
      const label = text(heading) || description.dataset.variantsLabel;
      const box = disclosure(label);
      section.before(box.details); box.body.append(section);
      heading?.remove();
    });
    removeDuplicateBulbs(root, description);
  }
  const start = () => document.querySelectorAll('product-page.nlc-product').forEach(enhance);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('reload', start);
})();



